import Point from "@arcgis/core/geometry/Point";
import {
  load as loadProjection,
  project,
} from "@arcgis/core/geometry/projection";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import IdentityManager from "@arcgis/core/identity/IdentityManager";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import {
  contains,
  generalize,
  union,
} from "@arcgis/core/geometry/geometryEngine";
import Polygon from "@arcgis/core/geometry/Polygon";
import Geometry from "@arcgis/core/geometry/Geometry";
import FeatureSet from "@arcgis/core/rest/support/FeatureSet";
import { encodeFormData } from "./shape-util";
import Extent from "@arcgis/core/geometry/Extent";
import {
  buildWebMapAsJson,
  PrintWithBasemap,
} from "./util/web-map-as-json-template";

export type IdentityManagerRegisterTokenProps = {
  server: string;
  token: string;
  expires: number;
};

async function PrintToPNG(
  token: string,
  printServiceUrl: string,
  mapServerUrl: string,
  baseLayerUrl: string,
  extent: Extent,
  definitionExpression: string,
  printWithBasemap: PrintWithBasemap,
  title: string,
  scale: number | null = null,
): Promise<MapImage> {
  // If a specific scale was provided, ensure the given shape's extent would fit in a map rendered in our
  // Layout_Template
  if (scale) {
    if (
      extent.width > (scale * 5_944.0) / 24_000.0 ||
      extent.height > (scale * 3_632.0) / 24_000.0
    ) {
      // Arbitrary width and height (in meters) of a shape that can fit within our Letter ANSI A Landscape
      // template's map at 1:24_000 scale.
      return { mapImageUrl: "", scale, shapeFitInScale: false };
    }
  }

  const promise = await fetch(printServiceUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: encodeFormData([
      ["token", token],
      [
        "Web_Map_as_JSON",
        buildWebMapAsJson(
          token,
          mapServerUrl,
          baseLayerUrl,
          extent,
          definitionExpression,
          printWithBasemap,
          title,
          scale,
        ),
      ],
      ["Layout_Template", "Letter ANSI A Landscape"],
      ["Format", "PNG8"],
      ["printFlag", "true"],
      ["f", "json"],
    ]),
  })
    .then((response) => response.json())
    .then((result) => {
      if (result["error"]) throw new Error(JSON.stringify(result["error"]));
      return result;
    })
    .then(
      (result: {
        results: {
          value: {
            url: string;
          };
        }[];
      }) => {
        return result.results[0].value.url;
      },
    );
  return { mapImageUrl: promise, scale: scale ?? 0, shapeFitInScale: true };
}

async function QueryUTM(
  token: string,
  UTMLayerURL: string,
  point: Point,
): Promise<Coordinates> {
  return Promise.all([
    fetch(`${UTMLayerURL}/query`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: encodeFormData([
        ["geometry", `${point.x},${point.y}`],
        ["geometryType", "esriGeometryPoint"],
        ["outFields", "ZONE_, ROW"],
        ["returnGeometry", "false"],
        ["f", "pjson"],
        ["token", token],
      ]),
    })
      .then((response) => response.json())
      .then(
        (result) =>
          `${result.features[0].attributes.ZONE_} ${result.features[0].attributes.ROW}`,
      ),
    loadProjection(),
    Promise.resolve(point),
  ]).then(([zone, _ignore, p]) => {
    const geometricPoint = project(p, SpatialReference.WGS84) as Point;
    return {
      utmsCoords: {
        zone,
        northing: p.y,
        easting: p.x,
      },
      latLongCoords: {
        latitude: geometricPoint.latitude,
        longitude: geometricPoint.longitude,
      },
    };
  });
}

export async function HCPILocationFetch(
  tokenProps: IdentityManagerRegisterTokenProps,
  ResourceId: number,
  ResourceLayerURL: string,
  UTMLayerURL: string,
  BaseLayerURL: string,
  iActivityNum: number | null = null,
  printServiceUrl: string | null = null,
  mapServerUrl: string | null = null,
  fixedScale: number | null = null,
  title: string | null = null,
): Promise<HCPILocationInfo> {
  console.debug("Registering token for server", tokenProps);
  IdentityManager.registerToken(tokenProps);
  try {
    const ResourceLayer = new FeatureLayer({
      url: ResourceLayerURL,
      minScale: 250000,
      outFields: ["*"],
      definitionExpression: `iResourceID = ${ResourceId}`,
    });

    const query = ResourceLayer.createQuery();
    query.maxRecordCountFactor = 0.001; // multiplied by the maxRecordCount on the layer
    const featureSet = await ResourceLayer.queryFeatures(query);
    if (featureSet.features.length === 0) return { error: false };
    const geometry = generalizePolygonIfBig(
      union(featureSet.features.map((g) => g.geometry)) as Polygon,
    );

    let mapImageUrl_dyn2_topo = null,
      mapImageUrl_fixed_topo = null,
      mapImageUrl_dyn2_hybrid = null,
      mapImageUrl_fixed_hybrid = null;
    if (printServiceUrl && mapServerUrl && title && iActivityNum) {
      [
        mapImageUrl_dyn2_topo,
        mapImageUrl_fixed_topo,
        mapImageUrl_dyn2_hybrid,
        mapImageUrl_fixed_hybrid,
      ] = await Promise.all([
        PrintToPNG(
          tokenProps.token,
          printServiceUrl,
          mapServerUrl,
          BaseLayerURL,
          geometry.extent.clone().expand(2.0),
          `iResourceID = ${ResourceId} and (ActivityNum = ${iActivityNum} or ActivityNum is null)`,
          PrintWithBasemap.UsaTOPO,
          title,
        ),
        PrintToPNG(
          tokenProps.token,
          printServiceUrl,
          mapServerUrl,
          BaseLayerURL,
          geometry.extent,
          `iResourceID = ${ResourceId} and (ActivityNum = ${iActivityNum} or ActivityNum is null)`,
          PrintWithBasemap.UsaTOPO,
          title,
          fixedScale,
        ),
        PrintToPNG(
          tokenProps.token,
          printServiceUrl,
          mapServerUrl,
          BaseLayerURL,
          geometry.extent.clone().expand(2.0),
          `iResourceID = ${ResourceId} and (ActivityNum = ${iActivityNum} or ActivityNum is null)`,
          PrintWithBasemap.Hybrid,
          title,
        ),
        PrintToPNG(
          tokenProps.token,
          printServiceUrl,
          mapServerUrl,
          BaseLayerURL,
          geometry.extent,
          `iResourceID = ${ResourceId} and (ActivityNum = ${iActivityNum} or ActivityNum is null)`,
          PrintWithBasemap.Hybrid,
          title,
          fixedScale,
        ),
      ]);
    }

    return {
      error: false,
      ...(await QueryUTM(tokenProps.token, UTMLayerURL, geometry.centroid)),
      mapImages: [
        mapImageUrl_dyn2_topo,
        mapImageUrl_fixed_topo,
        mapImageUrl_dyn2_hybrid,
        mapImageUrl_fixed_hybrid,
      ],
    };
  } catch (err) {
    console.error(err);
    return { error: true };
  }
}

async function QueryTopographicMaps(
  token: string,
  BaseLayerURL: string,
  geometry: Geometry,
): Promise<TopographicMap[]> {
  return fetch(`${BaseLayerURL}/4/query`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: encodeFormData([
      ["geometry", geometry],
      ["geometryType", "esriGeometryPolygon"],
      ["outFields", "Code, QuadName"],
      ["returnGeometry", "false"],
      ["f", "pjson"],
      ["token", token],
    ]),
  })
    .then((response) => response.json())
    .then((result: FeatureSet) =>
      result.features.map((f) => ({
        quadName: f.attributes.QuadName,
        quadCode: f.attributes.Code,
      })),
    );
}

async function QueryCounties(
  token: string,
  BaseLayerURL: string,
  geometry: Geometry,
): Promise<County[]> {
  return fetch(`${BaseLayerURL}/5/query`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: encodeFormData([
      ["geometry", geometry],
      ["geometryType", "esriGeometryPolygon"],
      ["outFields", "Name, FIPS"],
      ["returnGeometry", "false"],
      ["f", "pjson"],
      ["token", token],
    ]),
  })
    .then((response) => response.json())
    .then((result: FeatureSet) =>
      result.features.map((f) => ({
        name: f.attributes.Name,
        fips: f.attributes.FIPS,
      })),
    );
}

async function QueryPLSS(
  token: string,
  BaseLayerURL: string,
  geometry: Geometry,
): Promise<LegalDescription[]> {
  return fetch(`${BaseLayerURL}/3/query`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: encodeFormData([
      ["geometry", geometry],
      ["geometryType", "esriGeometryPolygon"],
      ["outFields", "Township, Range, SectionNum, plss"],
      ["returnGeometry", "false"],
      ["f", "pjson"],
      ["token", token],
    ]),
  })
    .then((response) => response.json())
    .then((result: FeatureSet) =>
      result.features.map((f) => {
        const unplatted =
          f.attributes.Township == "NS" &&
          f.attributes.Range == "NS" &&
          f.attributes.SectionNum == 0;
        return {
          unplatted,
          township: !unplatted ? f.attributes.Township : null,
          range: !unplatted ? f.attributes.Range : null,
          section: !unplatted ? f.attributes.SectionNum : null,
        };
      }),
    );
}

export async function NIAFLocationFetch(
  tokenProps: IdentityManagerRegisterTokenProps,
  ActivityNumber: number,
  ActivityLayerURL: string,
  BaseLayerURL: string,
  printServiceUrl: string | null = null,
  mapServerUrl: string | null = null,
  fixedScale: number | null = null,
  title: string | null = null,
): Promise<NIAFLocationInfo> {
  console.debug("Registering token for server", tokenProps);
  IdentityManager.registerToken(tokenProps);
  try {
    const activityLayer = new FeatureLayer({
      url: ActivityLayerURL,
      outFields: ["*"],
      definitionExpression: `iActivityNum = ${ActivityNumber}`,
    });

    const query = activityLayer.createQuery();
    query.maxRecordCountFactor = 0.001;

    const featureSet = await activityLayer.queryFeatures(query);
    if (featureSet.features.length === 0) return { error: false };
    const geometry = generalizePolygonIfBig(
      union(featureSet.features.map((g) => g.geometry)) as Polygon,
    );
    const [topographicMaps, counties, legalDescriptions] = await Promise.all([
      QueryTopographicMaps(tokenProps.token, BaseLayerURL, geometry),
      QueryCounties(tokenProps.token, BaseLayerURL, geometry),
      QueryPLSS(tokenProps.token, BaseLayerURL, geometry),
    ]);
    let mapImageUrl_dyn2_topo = null,
      mapImageUrl_fixed_topo = null,
      mapImageUrl_dyn2_hybrid = null,
      mapImageUrl_fixed_hybrid = null;
    if (printServiceUrl && mapServerUrl && title) {
      [
        mapImageUrl_dyn2_topo,
        mapImageUrl_fixed_topo,
        mapImageUrl_dyn2_hybrid,
        mapImageUrl_fixed_hybrid,
      ] = await Promise.all([
        PrintToPNG(
          tokenProps.token,
          printServiceUrl,
          mapServerUrl,
          BaseLayerURL,
          geometry.extent.clone().expand(2.0),
          `iActivityNum = ${ActivityNumber}`,
          PrintWithBasemap.UsaTOPO,
          title,
        ),
        PrintToPNG(
          tokenProps.token,
          printServiceUrl,
          mapServerUrl,
          BaseLayerURL,
          geometry.extent,
          `iActivityNum = ${ActivityNumber}`,
          PrintWithBasemap.UsaTOPO,
          title,
          fixedScale,
        ),
        PrintToPNG(
          tokenProps.token,
          printServiceUrl,
          mapServerUrl,
          BaseLayerURL,
          geometry.extent.clone().expand(2.0),
          `iActivityNum = ${ActivityNumber}`,
          PrintWithBasemap.Hybrid,
          title,
        ),
        PrintToPNG(
          tokenProps.token,
          printServiceUrl,
          mapServerUrl,
          BaseLayerURL,
          geometry.extent,
          `iActivityNum = ${ActivityNumber}`,
          PrintWithBasemap.Hybrid,
          title,
          fixedScale,
        ),
      ]);
    }
    return {
      error: false,
      topographicMaps,
      counties,
      legalDescriptions,
      mapImages: [
        mapImageUrl_dyn2_topo,
        mapImageUrl_fixed_topo,
        mapImageUrl_dyn2_hybrid,
        mapImageUrl_fixed_hybrid,
      ],
    };
  } catch (err) {
    console.error(err);
    return { error: true };
  }
}

export async function LALocationFetch(
  tokenProps: IdentityManagerRegisterTokenProps,
  ResourceId: number,
  ResourceLayerURL: string,
  BaseLayerURL: string,
  UTMLayerURL: string,
  iActivityNum: number | null = null,
  printServiceUrl: string | null = null,
  mapServerUrl: string | null = null,
  fixedScale: number | null = null,
  title: string | null = null,
): Promise<LALocationInfo> {
  console.debug("Registering token for server", tokenProps);
  IdentityManager.registerToken(tokenProps);

  const resourceLayer = new FeatureLayer({
    url: ResourceLayerURL,
    outFields: ["*"],
    definitionExpression: `iResourceID = ${ResourceId}`,
  });

  const query = resourceLayer.createQuery();
  query.maxRecordCountFactor = 0.001;

  try {
    const featureSet = await resourceLayer.queryFeatures(query);
    if (featureSet.features.length === 0) return { error: false };
    const geometry = generalizePolygonIfBig(
      union(featureSet.features.map((g) => g.geometry)) as Polygon,
    );
    const centerInside = contains(geometry, (geometry as Polygon).centroid);
    const [topographicMaps, counties, legalDescriptions, utmInfo] =
      await Promise.all([
        QueryTopographicMaps(tokenProps.token, BaseLayerURL, geometry),
        QueryCounties(tokenProps.token, BaseLayerURL, geometry),
        QueryPLSS(tokenProps.token, BaseLayerURL, geometry),
        QueryUTM(tokenProps.token, UTMLayerURL, (geometry as Polygon).centroid),
      ]);
    let mapImageUrl_dyn2_topo = null,
      mapImageUrl_fixed_topo = null,
      mapImageUrl_dyn2_hybrid = null,
      mapImageUrl_fixed_hybrid = null;
    if (printServiceUrl && mapServerUrl && title) {
      [
        mapImageUrl_dyn2_topo,
        mapImageUrl_fixed_topo,
        mapImageUrl_dyn2_hybrid,
        mapImageUrl_fixed_hybrid,
      ] = await Promise.all([
        PrintToPNG(
          tokenProps.token,
          printServiceUrl,
          mapServerUrl,
          BaseLayerURL,
          geometry.extent.clone().expand(2.0),
          `iResourceID = ${ResourceId} and (ActivityNum = ${iActivityNum} or ActivityNum is null)`,
          PrintWithBasemap.UsaTOPO,
          title,
        ),
        PrintToPNG(
          tokenProps.token,
          printServiceUrl,
          mapServerUrl,
          BaseLayerURL,
          geometry.extent,
          `iResourceID = ${ResourceId} and (ActivityNum = ${iActivityNum} or ActivityNum is null)`,
          PrintWithBasemap.UsaTOPO,
          title,
          fixedScale,
        ),
        PrintToPNG(
          tokenProps.token,
          printServiceUrl,
          mapServerUrl,
          BaseLayerURL,
          geometry.extent.clone().expand(2.0),
          `iResourceID = ${ResourceId} and (ActivityNum = ${iActivityNum} or ActivityNum is null)`,
          PrintWithBasemap.Hybrid,
          title,
        ),
        PrintToPNG(
          tokenProps.token,
          printServiceUrl,
          mapServerUrl,
          BaseLayerURL,
          geometry.extent,
          `iResourceID = ${ResourceId} and (ActivityNum = ${iActivityNum} or ActivityNum is null)`,
          PrintWithBasemap.Hybrid,
          title,
          fixedScale,
        ),
      ]);
    }
    return {
      error: false,
      topographicMaps,
      counties,
      legalDescriptions,
      ...utmInfo,
      centerInside,
      mapImages: [
        mapImageUrl_dyn2_topo,
        mapImageUrl_fixed_topo,
        mapImageUrl_dyn2_hybrid,
        mapImageUrl_fixed_hybrid,
      ],
    };
  } catch (err) {
    console.error(err);
    return { error: true };
  }
}

export async function SRLocationFetch(
  tokenProps: IdentityManagerRegisterTokenProps,
  ResourceId: number,
  ResourceLayerURL: string,
  BaseLayerURL: string,
  printServiceUrl: string | null = null,
  mapServerUrl: string | null = null,
  title: string | null = null,
): Promise<SRLocationInfo> {
  console.debug("Registering token for server", tokenProps);
  IdentityManager.registerToken(tokenProps);

  const resourceLayer = new FeatureLayer({
    url: ResourceLayerURL,
    outFields: ["*"],
    definitionExpression: `iResourceID = ${ResourceId}`,
  });

  const query = resourceLayer.createQuery();
  query.maxRecordCountFactor = 0.001;

  try {
    const featureSet = await resourceLayer.queryFeatures(query);
    if (featureSet.features.length === 0) return { error: false };
    const geometry = generalizePolygonIfBig(
      union(featureSet.features.map((g) => g.geometry)) as Polygon,
    );
    let mapImageUrl_dyn2_topo = null,
      mapImageUrl_dyn2_hybrid = null;
    if (printServiceUrl && mapServerUrl && title) {
      [mapImageUrl_dyn2_topo, mapImageUrl_dyn2_hybrid] = await Promise.all([
        PrintToPNG(
          tokenProps.token,
          printServiceUrl,
          mapServerUrl,
          BaseLayerURL,
          geometry.extent.clone().expand(2.0),
          `iResourceID = ${ResourceId}`,
          PrintWithBasemap.UsaTOPO,
          title,
        ),
        PrintToPNG(
          tokenProps.token,
          printServiceUrl,
          mapServerUrl,
          BaseLayerURL,
          geometry.extent.clone().expand(2.0),
          `iResourceID = ${ResourceId}`,
          PrintWithBasemap.Hybrid,
          title,
        ),
      ]);
    }
    return {
      error: false,
      mapImages: [mapImageUrl_dyn2_topo, mapImageUrl_dyn2_hybrid],
    };
  } catch (err) {
    console.error(err);
    return { error: true };
  }
}

function generalizePolygonIfBig(polygon: Polygon): Polygon {
  const vertices = polygon.rings.reduce(
    (accumulator, currentValue) => accumulator + currentValue.length,
    0,
  );
  if (vertices > 250000) {
    console.debug(
      `Geometry vertex count ${vertices} exceeded 250,000. Generalizing.`,
    );
    polygon = generalize(polygon, 2.5) as Polygon;
    console.debug(
      `Vertex count post-generalize ${polygon.rings.reduce((accumulator, currentValue) => accumulator + currentValue.length, 0)}`,
    );
  }
  return polygon;
}

export async function convertUTMToLatLong(
  northing: number,
  easting: number,
): Promise<OrError & Partial<{ latLongCoords: LatLongCoords }>> {
  try {
    const point = new Point({
      x: easting,
      y: northing,
      spatialReference: new SpatialReference({
        wkid: 26913,
      }),
    });
    await loadProjection();
    const geometricPoint = project(point, SpatialReference.WGS84) as Point;
    return {
      error: false,
      latLongCoords: {
        latitude: geometricPoint.latitude,
        longitude: geometricPoint.longitude,
      },
    };
  } catch (err) {
    console.error(err);
    return { error: true };
  }
}
